// Sound effects manager for chess piece actions
// WebM audio files should be placed in public/sounds/

// Use PUBLIC_URL for proper path resolution in production
const BASE_PATH = process.env.PUBLIC_URL || '';

const SOUND_FILES = {
  moveSelf: `${BASE_PATH}/sounds/move-self.webm`,
  moveOpponent: `${BASE_PATH}/sounds/move-opponent.webm`,
  moveCheck: `${BASE_PATH}/sounds/move-check.webm`,
  capture: `${BASE_PATH}/sounds/capture.webm`,
  castle: `${BASE_PATH}/sounds/castle.webm`,
  promote: `${BASE_PATH}/sounds/promote.webm`,
  gameLose: `${BASE_PATH}/sounds/game-lose.mp3`,
  gameDraw: `${BASE_PATH}/sounds/game-draw.mp3`,
  purchase: `${BASE_PATH}/sounds/purchase.m4a`,
  sell: `${BASE_PATH}/sounds/sell2.m4a`,
  gameWin: `${BASE_PATH}/sounds/game-win.mp3`,
};

// Round win sounds - randomly selected
const ROUND_WIN_SOUNDS = [
  `${BASE_PATH}/sounds/round-win2.mp3`,
  `${BASE_PATH}/sounds/round-win3.mp3`,
  `${BASE_PATH}/sounds/round-win4.mp3`,
  `${BASE_PATH}/sounds/round-win5.mp3`,
];

// Track last two round win sounds to avoid three in a row
let roundWinHistory = [];

// Audio element cache for preloading
const audioCache = {};

// Whether sounds are enabled (can be toggled via settings)
let soundsEnabled = true;

/**
 * Preload all sound effects
 * Call this once on app startup to ensure sounds are ready to play
 */
export function preloadSounds() {
  Object.entries(SOUND_FILES).forEach(([key, src]) => {
    try {
      const audio = new Audio(src);
      audio.preload = 'auto';
      // Load the audio to cache it
      audio.load();
      audioCache[key] = audio;
    } catch (err) {
      console.warn(`[sounds] Failed to preload ${key}:`, err);
    }
  });
  // Preload round win sounds
  ROUND_WIN_SOUNDS.forEach((src, i) => {
    try {
      const audio = new Audio(src);
      audio.preload = 'auto';
      audio.load();
      audioCache[`roundWin${i}`] = audio;
    } catch (err) {
      console.warn(`[sounds] Failed to preload roundWin${i}:`, err);
    }
  });
}

/**
 * Play a sound effect by key
 * @param {string} soundKey - One of: moveSelf, moveOpponent, moveCheck, capture, castle, promote, gameLose, gameDraw, purchase, sell, roundWin, gameWin
 * @param {number} [volume=1.0] - Volume level from 0.0 to 1.0
 */
export function playSound(soundKey, volume = 1.0) {
  if (!soundsEnabled) return;
  
  let src;
  if (soundKey === 'roundWin') {
    // Randomly select a round win sound, avoiding three in a row
    let availableSounds = ROUND_WIN_SOUNDS;
    
    // If the same sound was played twice in a row, exclude it
    if (roundWinHistory.length >= 2 && 
        roundWinHistory[0] === roundWinHistory[1]) {
      availableSounds = ROUND_WIN_SOUNDS.filter(s => s !== roundWinHistory[0]);
    }
    
    const idx = Math.floor(Math.random() * availableSounds.length);
    src = availableSounds[idx];
    
    // Update history (keep only last 2)
    roundWinHistory.unshift(src);
    if (roundWinHistory.length > 2) {
      roundWinHistory.pop();
    }
  } else {
    src = SOUND_FILES[soundKey];
  }
  
  if (!src) {
    console.warn(`[sounds] Unknown sound key: ${soundKey}`);
    return;
  }
  
  try {
    // Create a new Audio instance each time to allow overlapping sounds
    const audio = new Audio(src);
    audio.volume = Math.max(0, Math.min(1, volume));
    audio.play().catch((err) => {
      // Browsers may block autoplay; ignore silently
      console.debug(`[sounds] Playback blocked for ${soundKey}:`, err);
    });
  } catch (err) {
    console.warn(`[sounds] Failed to play ${soundKey}:`, err);
  }
}

/**
 * Enable or disable sound effects
 * @param {boolean} enabled
 */
export function setSoundsEnabled(enabled) {
  soundsEnabled = !!enabled;
}

/**
 * Check if sounds are currently enabled
 * @returns {boolean}
 */
export function areSoundsEnabled() {
  return soundsEnabled;
}

// Sound effect helper functions for specific game events

/**
 * Play the appropriate sound for a chess move during battle
 * @param {Object} moveResult - chess.js move result object
 * @param {boolean} isWhiteMove - Whether the move was made by white (player)
 */
export function playMoveSound(moveResult, isWhiteMove) {
  if (!moveResult) return;
  
  const flags = moveResult.flags || '';
  const san = moveResult.san || '';
  
  // Priority order: check > promotion > capture > castle > regular move
  // Note: checkmate sound is handled separately (gameLose played on game over screen)
  
  // Check (including checkmate - both have + or #)
  if (san.includes('+') || san.includes('#')) {
    playSound('moveCheck');
    return;
  }
  
  // Promotion
  if (flags.includes('p')) {
    playSound('promote');
    return;
  }
  
  // Capture (including en passant 'e')
  if (flags.includes('c') || flags.includes('e')) {
    playSound('capture', 0.8);
    return;
  }
  
  // Castling (kingside 'k' or queenside 'q')
  if (flags.includes('k') || flags.includes('q')) {
    playSound('castle');
    return;
  }
  
  // Regular move - only play for white (player) moves at reduced volume
  if (isWhiteMove) {
    playSound('moveSelf', 0.8);
  }
}

/**
 * Play sound for piece movement during setup/prep phase
 * Uses the castle sound for all setup movements
 */
export function playSetupMoveSound() {
  playSound('castle');
}

export default {
  preloadSounds,
  playSound,
  playMoveSound,
  playSetupMoveSound,
  setSoundsEnabled,
  areSoundsEnabled,
};

